import { useEffect, useMemo, useState } from "react"
import { Space, Checkbox, Flex, Button } from "antd"
import {
    DndContext, closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor, useSensors
} from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    verticalListSortingStrategy,
    defaultAnimateLayoutChanges,
    useSortable
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { LockOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";



const ColumnsSelector = (props) => {
    const { defaultViewColumns, defaultSelectedColumns = [] } = props;
    const { t } = useTranslation();
    
    // Initialize selectedColumns from defaultSelectedColumns immediately
    const [selectedColumns, setSelectedColumns] = useState(() => {
        return defaultSelectedColumns ? defaultSelectedColumns.map(col => col.id) : []
    })
    
    const [columnsView, setColumnsView] = useState([])

    // Remove duplicates from defaultViewColumns based on 'value' field
    const uniqueColumns = useMemo(() => {
        const seen = new Set();
        return defaultViewColumns.filter(col => {
            if (seen.has(col.value)) {
                return false;
            }
            seen.add(col.value);
            return true;
        });
    }, [defaultViewColumns]);

    // Derived helpers for select-all
    const requiredValues = useMemo(() => (columnsView || []).filter(c => c.isDisabled).map(c => c.value), [columnsView])
    const selectableValues = useMemo(() => (columnsView || []).filter(c => !c.isDisabled).map(c => c.value), [columnsView])
    const allChecked = useMemo(() => selectableValues.length > 0 && selectableValues.every(v => selectedColumns.includes(v)), [selectableValues, selectedColumns])
    const someChecked = useMemo(() => selectableValues.some(v => selectedColumns.includes(v)), [selectableValues, selectedColumns])

    // Update selectedColumns when defaultSelectedColumns changes
    useEffect(() => {
        if (defaultSelectedColumns) {
            let selCols = defaultSelectedColumns.map(col => col.id)
            setSelectedColumns(selCols)
        }
    }, [defaultSelectedColumns])

    // Update columnsView when uniqueColumns or selectedColumns change
    // Selected columns maintain their order from selectedColumns state (table order), unselected are sorted A-Z
    useEffect(() => {
        // Get selected columns and sort them based on their position in selectedColumns array
        const selectedCols = uniqueColumns
            .filter(col => selectedColumns.includes(col.value))
            .sort((a, b) => {
                // Sort by their index in selectedColumns to maintain table order
                const indexA = selectedColumns.indexOf(a.value);
                const indexB = selectedColumns.indexOf(b.value);
                return indexA - indexB;
            });
        
        // Get unselected columns and sort them alphabetically
        const columnsNotSelected = uniqueColumns
            .filter(col => !selectedColumns.includes(col.value))
            .sort((a, b) => a.label.localeCompare(b.label));
        
        // Combine: selected columns first (in table order), then unselected (sorted A-Z)
        const orderedColumns = [...selectedCols, ...columnsNotSelected];
        setColumnsView(orderedColumns);
    }, [uniqueColumns, selectedColumns])

    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates
        })
    );

    const handleColumnsChange = (val) => {
        setSelectedColumns(val)
    }

    const handleToggleAll = (e) => {
        if (e.target.checked) {
            // Select all, including required
            setSelectedColumns(Array.from(new Set([
                ...requiredValues, ...selectableValues
            ])))
        } else {
            // Keep required only
            setSelectedColumns([...requiredValues])
        }
    }

    const handleReset = () => {
        if (defaultSelectedColumns && defaultSelectedColumns.length > 0) {
            const sel = defaultSelectedColumns.map(col => col.id)
            setSelectedColumns(sel)
        } else {
            // fallback to required only if no defaults
            setSelectedColumns([...requiredValues])
        }
    }

    const handleDragEnd = (event) => {
        const { active, over } = event;
        if (active.id === 'documentId' || over.id === 'documentId') {
            return;
        }

        if (active.id !== over.id) {
            setColumnsView((items) => {
                const oldIndex = active.data.current.sortable.index;
                const newIndex = over.data.current.sortable.index;

                return arrayMove(items, oldIndex, newIndex);
            });
        }
    }

    const handleColumnsSave = () => {
        // Get all selected columns in the order they appear in columnsView
        let selCols = columnsView
            .filter(col => selectedColumns.includes(col.value))
            .map(col => col.value)
        props.saveSelectedColumns(selCols)
    }


    return (
        <div style={{ maxWidth: "600px", overflow: "hidden" }}>
            <DndContext sensors={sensors} collisionDetection={closestCenter} modifiers={[restrictToVerticalAxis]}
                onDragEnd={handleDragEnd}>
                <SortableContext items={columnsView} strategy={verticalListSortingStrategy}>
                    <Space direction="vertical" className="columns-selector-container">
                        <div style={{ padding: "0 24px 8px" }}>
                            <Checkbox
                                indeterminate={!allChecked && someChecked}
                                checked={allChecked}
                                onChange={handleToggleAll}
                            >{t("Select all")}</Checkbox>
                        </div>
                        <Checkbox.Group value={selectedColumns} onChange={handleColumnsChange} style={{ display: "grid" }}>
                            {
                                columnsView.map(option => {
                                    return (
                                        <SortableItem key={`column-${option.value}`} id={option.value} option={option} />
                                    )
                                })
                            }
                        </Checkbox.Group>

                    </Space>
                </SortableContext>
            </DndContext>
            <Flex justify="flex-end" gap={8} align="center" style={{ padding: "8px 24px 24px" }}>
                <Button default style={{ border: "none" }} onClick={handleReset}>{t("Reset")}</Button>
                <Button type="primary" onClick={handleColumnsSave}>{t("Save")}</Button>
            </Flex>
        </div>

    )

}

export default ColumnsSelector

function SortableItem({ id, option }) {
    const animateLayoutChanges = (args) =>
        args.isSorting || args.wasDragging ? defaultAnimateLayoutChanges(args) : true;

    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition
    } = useSortable({
        animateLayoutChanges,
        id: id
    });


    const style = {
        transform: CSS.Translate.toString(transform),
        transition
    };
    return (
        <div className="checkbox-row" ref={setNodeRef} style={style} {...attributes}>
            <Checkbox value={option.value} style={{ color: "#000000E0" }} disabled={option.isDisabled}>
                {option.label}
                {option.sectionLabel ? ` (${option.sectionLabel})` : ""}
            </Checkbox>
            {
                id !== 'documentId' ?
                    <button className="DragHandle" {...listeners} >
                        <svg viewBox="0 0 20 20" width="12">
                            <path d="M7 2a2 2 0 1 0 .001 4.001A2 2 0 0 0 7 2zm0 6a2 2 0 1 0 .001 4.001A2 2 0 0 0 7 8zm0 6a2 2 0 1 0 .001 4.001A2 2 0 0 0 7 14zm6-8a2 2 0 1 0-.001-4.001A2 2 0 0 0 13 6zm0 2a2 2 0 1 0 .001 4.001A2 2 0 0 0 13 8zm0 6a2 2 0 1 0 .001 4.001A2 2 0 0 0 13 14z"></path>
                        </svg>
                    </button>
                    : <button style={{ background: "#fff", border: "none" }} disabled>
                        <LockOutlined />
                    </button>
            }
        </div>
    );
}